// src/features/payment/ChipListInput.tsx
// Chip-based add/remove editor for short multi-value lists (currencies,
// countries, ...). Replaces comma-separated text blobs per docs/ux/14-forms.md
// ("multi-value lists should be chip-based add/remove editors"). Domain-local
// to payment — modeled on the interaction in src/components/TagPicker.tsx but
// without the search/remote-fetch machinery this domain doesn't need.

import { useState, type KeyboardEvent } from 'react';
import { cn } from '@core/ui';
import { useI18n } from '@react/hooks';

export interface ChipListInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Applied to each entry before it's added, e.g. uppercase for ISO codes. */
  normalize?: (raw: string) => string;
  className?: string;
  'aria-label'?: string;
}

export function ChipListInput({
  value,
  onChange,
  placeholder,
  normalize,
  className,
  'aria-label': ariaLabel,
}: ChipListInputProps) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');

  const addFromDraft = () => {
    const raw = draft.trim();
    setDraft('');
    if (!raw) return;
    const entry = normalize ? normalize(raw) : raw;
    if (!entry) return;
    if (value.some((v) => v.toLowerCase() === entry.toLowerCase())) return;
    onChange([...value, entry]);
  };

  const remove = (entry: string) => {
    onChange(value.filter((v) => v !== entry));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addFromDraft();
      return;
    }
    if (e.key === 'Backspace' && !draft && value.length > 0) {
      remove(value[value.length - 1]);
    }
  };

  return (
    <div
      className={cn(
        'flex min-h-[34px] w-full flex-wrap items-center gap-1.5 rounded-md border border-dashed border-input bg-transparent px-2 py-1.5',
        'focus-within:border-solid focus-within:ring-2 focus-within:ring-ring',
        className
      )}
    >
      {value.map((entry) => (
        <span
          key={entry}
          className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 font-mono text-xs text-foreground"
        >
          {entry}
          <button
            type="button"
            onClick={() => remove(entry)}
            aria-label={t('common.remove')}
            className="text-muted-foreground hover:text-foreground"
          >
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={addFromDraft}
        placeholder={value.length === 0 ? placeholder : undefined}
        aria-label={ariaLabel || placeholder}
        autoCapitalize="off"
        className="min-w-[64px] flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
      />
    </div>
  );
}

export default ChipListInput;
